import { familyColours, familyKey, lineTouches, L, type Line } from '@lineage/core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PersonCard } from './PersonCard';
import { clampZoom, useView } from './store';
import type { TreeModel } from './model';
import s from './TreeCanvas.module.css';
import { useT } from '../../i18n';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
const DRAG_THRESHOLD = 5;

interface Props {
  model: TreeModel;
  meId: string | null;
  /** Fraction of the viewport height at which a selected card is centred (mobile sheet open → 0.22). */
  focusY?: number;
  /** 'auto' centres on the viewer in very large trees; 'fit' always opens on the whole tree. */
  frame?: 'auto' | 'fit';
  onSelect?: (id: string | null) => void;
  /** Bumped by the parent to ask for a refit / re-centre. */
  commandKey?: number;
}

export function TreeCanvas({ model, meId, focusY = 0.5, onSelect, commandKey, frame = 'auto' }: Props) {
  const { layout, graph, labels, calls, pack } = model;
  const t = useT().tree;
  const ref = useRef<HTMLDivElement>(null);
  const view = useView();
  const selectedId = view.selectedId;
  const [panning, setPanning] = useState(false);

  const size = () => {
    const r = ref.current?.getBoundingClientRect();
    return { w: r?.width ?? 800, h: r?.height ?? 600 };
  };

  const fit = useCallback(() => {
    const { w, h } = size();
    const k = clampZoom(Math.min((w - 24) / layout.width, (h - 24) / layout.height, 1));
    useView.getState().set({ k, x: (w - layout.width * k) / 2, y: Math.max(12, (h - layout.height * k) / 2) });
  }, [layout.width, layout.height]);

  const centreOn = useCallback(
    (id: string, fy = 0.5, minK = 0.6) => {
      const p = layout.positions.get(id);
      if (!p) return;
      const { w, h } = size();
      const k = Math.max(useView.getState().k, minK);
      useView.getState().set({ k, x: w / 2 - p.x * k, y: h * fy - (p.y + L.portraitY) * k });
    },
    [layout],
  );

  // Initial framing: fit small trees; centre on the viewer in large ones.
  const framed = useRef(false);
  useLayoutEffect(() => {
    if (framed.current || layout.positions.size === 0) return;
    framed.current = true;
    const { w, h } = size();
    const k = Math.min((w - 24) / layout.width, (h - 24) / layout.height, 1);
    if (frame === 'auto' && k < 0.5 && meId && layout.positions.has(meId)) {
      useView.getState().set({ k: 0.8 });
      centreOn(meId, 0.4);
    } else fit();
  }, [layout, meId, fit, centreOn, frame]);

  // Parent commands (Fit button / find on canvas).
  const lastCmd = useRef(commandKey);
  useEffect(() => {
    if (commandKey === lastCmd.current) return;
    lastCmd.current = commandKey;
    if (selectedId) centreOn(selectedId, focusY);
    else fit();
  }, [commandKey, selectedId, focusY, centreOn, fit]);

  // Centre the selected card (in the top part of the screen when a sheet covers the bottom).
  useEffect(() => {
    if (selectedId) centreOn(selectedId, focusY);
    onSelect?.(selectedId);
  }, [selectedId, focusY, centreOn, onSelect]);

  // Wheel: plain wheel pans; Ctrl/⌘ + wheel zooms at the cursor.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const st = useView.getState();
      if (e.ctrlKey || e.metaKey) {
        const r = el.getBoundingClientRect();
        st.zoomAt(e.clientX - r.left, e.clientY - r.top, st.k * Math.exp(-e.deltaY * 0.01));
      } else st.set({ x: st.x - e.deltaX, y: st.y - e.deltaY });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Pointer pan (5 px threshold so taps stay taps) and two-finger pinch.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);
  const pinch = useRef<{ d: number; k: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const st = useView.getState();
    if (pointers.current.size === 1) drag.current = { sx: e.clientX, sy: e.clientY, ox: st.x, oy: st.y, moved: false };
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y), k: st.k };
      drag.current = null;
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const st = useView.getState();
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const r = ref.current!.getBoundingClientRect();
      st.zoomAt(
        (a.x + b.x) / 2 - r.left,
        (a.y + b.y) / 2 - r.top,
        pinch.current.k * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.current.d),
      );
      return;
    }
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      d.moved = true;
      ref.current?.setPointerCapture(e.pointerId);
      setPanning(true);
    }
    if (d.moved) st.set({ x: d.ox + dx, y: d.oy + dy });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    const d = drag.current;
    if (pointers.current.size === 0) {
      // A tap on empty space deselects.
      if (d && !d.moved && !(e.target as HTMLElement).closest('[data-person]')) useView.getState().select(null);
      drag.current = null;
      setPanning(false);
    }
  };

  // Arrow keys move the selection: up → parent, down → child, left/right → partner or sibling.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!selectedId || !e.key.startsWith('Arrow')) return;
    e.preventDefault();
    const x = (id: string) => layout.positions.get(id)?.x ?? 0;
    const pick = (ids: string[], dir: 1 | -1 | 0) => {
      if (!ids.length) return null;
      if (dir === 0) return ids[0]!;
      const cur = x(selectedId);
      const sorted = [...ids].sort((a, b) => x(a) - x(b));
      return (dir === 1 ? sorted.find((i) => x(i) > cur) : [...sorted].reverse().find((i) => x(i) < cur)) ?? null;
    };
    const near = [
      ...(graph.partners.get(selectedId) ?? []),
      ...[...(graph.parents.get(selectedId) ?? [])]
        .flatMap((p) => graph.children.get(p) ?? [])
        .filter((c) => c !== selectedId),
    ];
    const next =
      e.key === 'ArrowUp'
        ? pick(graph.parents.get(selectedId) ?? [], 0)
        : e.key === 'ArrowDown'
          ? pick(graph.children.get(selectedId) ?? [], 0)
          : e.key === 'ArrowRight'
            ? pick(near, 1)
            : pick(near, -1);
    if (next) {
      useView.getState().select(next);
      requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>(`[data-person="${next}"]`)?.focus());
    }
  };

  const mePos = meId ? layout.positions.get(meId) : undefined;
  const colours = useMemo(() => familyColours(layout), [layout]);
  const hasPartial = useMemo(() => layout.lines.some((l) => l.kind === 'child' && l.partial), [layout.lines]);
  const lines = useMemo(() => {
    const normal: Line[] = [];
    const lit: Line[] = [];
    const couples: Line[] = [];
    for (const l of layout.lines) {
      if (l.kind === 'couple') couples.push(l);
      else (lineTouches(l, selectedId) ? lit : normal).push(l);
    }
    return { normal, lit, couples };
  }, [layout.lines, selectedId]);

  const zoomBy = (f: number) => {
    const { w, h } = size();
    view.zoomAt(w / 2, h / 2, view.k * f);
  };

  return (
    <div
      ref={ref}
      className={`${s.viewport} ${panning ? s.panning : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
      role="application"
      aria-label={t.familyTree}
    >
      <div
        className={s.world}
        style={{
          width: layout.width,
          height: layout.height,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
        }}
      >
        <svg className={s.lines} width={layout.width} height={layout.height} aria-hidden="true">
          {lines.normal.map((l, i) => (
            <LinePath key={`n${i}`} line={l} lit={false} dim={Boolean(selectedId)} colour={colours} />
          ))}
          {lines.lit.map((l, i) => (
            <LinePath key={`l${i}`} line={l} lit colour={colours} />
          ))}
          {/* Couple connectors and their diamonds go last so child lines emerge from underneath them. */}
          {lines.couples.map((l, i) => (
            <LinePath key={`c${i}`} line={l} lit={false} colour={colours} />
          ))}
        </svg>
        {layout.rows.map((row) => (
          <div key={row.gen} className={s.rail} style={{ top: row.y + 36 }} aria-hidden="true">
            <div className={s.roman}>{ROMAN[row.gen] ?? row.gen + 1}</div>
            {mePos && <div className={s.caption} lang={pack.lang} dir={pack.dir}>{pack.generation(row.gen - mePos.gen)}</div>}
          </div>
        ))}
        {[...layout.positions].map(([id, p]) => {
          const person = graph.people.get(id)!;
          return (
            <PersonCard
              key={id}
              person={person}
              relation={labels.get(id) ?? ''}
              call={calls.get(id) ?? ''}
              lang={pack.lang}
              dir={pack.dir}
              isMe={id === meId}
              selected={id === selectedId}
              x={p.x}
              y={p.y}
              onSelect={(pid) => useView.getState().select(pid)}
            />
          );
        })}
      </div>
      {hasPartial && (
        <div className={s.legend} role="group" aria-label={t.lineKey}>
          <span className={s.legendItem}><svg width="26" height="8" aria-hidden="true"><path d="M0 4H26" stroke="currentColor" strokeWidth="2" /></svg>{t.childOfBoth}</span>
          <span className={s.legendItem}><svg width="26" height="8" aria-hidden="true"><path d="M0 4H26" stroke="currentColor" strokeWidth="2" strokeDasharray="5 4" /></svg>{t.childOfOne}</span>
        </div>
      )}
      <div className={s.controls}>
        <button type="button" aria-label={t.zoomIn} onClick={() => zoomBy(1.25)}>
          +
        </button>
        <button type="button" aria-label={t.zoomOut} onClick={() => zoomBy(0.8)}>
          −
        </button>
        <button type="button" aria-label={t.fitScreen} onClick={fit}>
          ⤢
        </button>
      </div>
    </div>
  );
}

function LinePath({ line, lit, dim = false, colour }: { line: Line; lit: boolean; dim?: boolean; colour: ReadonlyMap<string, number> }) {
  if (line.kind === 'couple') {
    const d = L.diamond;
    return (
      <g>
        <path d={line.d} stroke="var(--brass)" strokeWidth={1.5} fill="none" />
        <rect
          x={line.mx - d / 2}
          y={line.my - d / 2}
          width={d}
          height={d}
          fill="var(--brass)"
          transform={`rotate(45 ${line.mx} ${line.my})`}
        />
      </g>
    );
  }
  return (
    <path
      d={line.d}
      stroke={lit ? 'var(--brass)' : `var(--edge-${colour.get(familyKey(line.household)) ?? 0})`}
      strokeDasharray={line.partial ? '7 6' : undefined}
      strokeWidth={lit ? 2.6 : 2.1}
      opacity={dim ? 0.35 : 1}
      fill="none"
      strokeLinecap="round"
    />
  );
}
